import { acquireApiToken } from '@/app/core/auth/authToken'

/**
 * Thin REST client for the modules whose backend speaks REST (`/api/v1/*`) rather than
 * GraphQL — accounting, inventory reports and QC. Mirrors the Apollo auth link: attach the B2C
 * bearer token when present, otherwise stay a no-op so the backend mock-admin chain keeps serving
 * in dev.
 *
 * Deliberately does NOT auto-redirect to re-authenticate on 401. Doing so loops: the redirect
 * returns to the page, the same request fires again, and if the token still cannot be acquired it
 * redirects again, forever. A 401 is surfaced to the caller as an ApiError; recovering the session
 * belongs to the auth layer, which can reason about whether a fresh token is actually obtainable.
 */
const API_BASE = (import.meta.env?.VITE_API_BASE as string | undefined) || '/api/v1'

export interface ApiError extends Error {
  status: number
  body?: unknown
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await acquireApiToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown
    let message = `${res.status} ${res.statusText}`
    try {
      body = await res.json()
      if (body && typeof body === 'object') {
        const b = body as Record<string, unknown>
        message = (b.message as string) || (b.error as string) || (b.detail as string) || message
      }
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(message) as ApiError
    err.status = res.status
    err.body = body
    throw err
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...(await authHeaders()) },
  })
  return handle<T>(res)
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return handle<T>(res)
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return handle<T>(res)
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: { ...(await authHeaders()) },
  })
  return handle<T>(res)
}

/**
 * Multipart upload through the same authed client as the JSON verbs. Deliberately omits the
 * `Content-Type` header so the browser sets the multipart boundary itself; setting it manually
 * corrupts the body. Routes error handling through the shared `handle()` so uploads behave like
 * every other REST call (auth token attached, ApiError surfaced, no 401 redirect loop).
 */
export async function apiUpload<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { ...(await authHeaders()) },
    body: form,
  })
  return handle<T>(res)
}

/** Build a query string from a params object, skipping null/undefined values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (entries.length === 0) return ''
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
}
